/**
 * DRPX Courier — Storage- und Foto-Funktionen (Supabase)
 *
 * File-Uploads in die verschiedenen Storage-Buckets, Signed-URL-Erzeugung,
 * KYC-Dokumente, Auftragsfoto-Dokumentation. Alle Funktionen liefern das
 * einheitliche `ApiResult<T>`-Shape (`{ data, error }`).
 *
 * Aufruf-Pattern für Caller:
 *   - **Mutationen** (Uploads, Delete) → via `api()` mit Toast-Feedback:
 *     `await api(db.uploadKycDoc(...), openToast, t, 'uploadKycDoc')`
 *   - **Reads** (URLs, Counts, Listen) → via `apiSilent()` ohne UI-Feedback,
 *     aber mit zentralem Logging via `logError()`:
 *     `await apiSilent(db.getSignedUrl(...), 'getSignedUrl')`
 *
 * Ausnahmen vom Pattern: `getKycUrl` in ProfilSeite (User klickt Doc, will
 * spezifischen Fehlertext sehen → direkter Aufruf mit Toast) und
 * `frachtbriefLesen` in FahrerEigeneAuftraege (eigener Text je
 * Fehlercode → direkter Aufruf).
 *
 * @module lib/supabase/storage
 */

import { _sb } from './client.js';
import { mapAuftragFotoFromDb } from './mappers.js';
import { updateAuftrag } from './auftraege.js';
import { updateProfile, getProfile } from './profiles.js';
import {
  validateUploadFileDeep,
  validateUploadBlobDeep,
  sanitizeFileName,
  type BucketName,
} from '../upload.js';
import { todayLocal } from '../format.js';
import type { DokReviewEntry } from '../../types';

// ════════════════════════════════════════════════════════════════════
// Interne Helper
// ════════════════════════════════════════════════════════════════════

/**
 * Einheitliche Fehler-Instanz, wenn der Supabase-Client nicht initialisiert
 * ist (z.B. wenn die .env-Variablen fehlen).
 */
const NO_CLIENT_ERROR = new Error('Supabase client not initialized');

/**
 * Extrahiert eine Fehler-Nachricht aus einem `unknown`-Wert (typisch aus
 * `catch (e: unknown)`).
 */
function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

// ════════════════════════════════════════════════════════════════════
// Lokale Interfaces (nicht exportiert)
// ════════════════════════════════════════════════════════════════════

/**
 * Unifiziertes Result-Shape für ALLE Storage-Funktionen.
 *
 * Discriminated Union: Entweder `data` ODER `error` ist gesetzt, nie beides.
 * Das garantiert TypeScript, dass Caller nach `if (res.error)` in den
 * Success-Branch fallen, wo `res.data` korrekt typisiert ist.
 *
 * Konvention:
 *  - Success → `{ data: T }`
 *  - Error   → `{ error: string }`
 *
 * Konsumiert von: allen exportierten Storage-Funktionen in diesem Modul.
 * Exportiert, damit Caller den Typ bei Bedarf importieren können.
 */
export type ApiResult<T> =
  | { data: T; error?: undefined }
  | { data?: undefined; error: string };

/**
 * Convenience-Helper: Baut ein Error-Result mit der passenden Shape.
 * Wandelt `UploadValidationError` oder `Error`-Instanzen in einen String um,
 * damit das Error-Feld immer ein einfacher String ist (einheitliche Caller-
 * Behandlung).
 */
function errResult<T>(e: unknown): ApiResult<T> {
  if (e instanceof Error) return { error: e.message };
  if (typeof e === 'string') return { error: e };
  // UploadValidationError ist ein Objekt — serialisieren als JSON
  if (typeof e === 'object' && e !== null) {
    const obj = e as { message?: string; code?: string };
    if (obj.message) return { error: obj.message };
    if (obj.code) return { error: obj.code };
    try { return { error: JSON.stringify(e) }; } catch { return { error: 'Unknown error' }; }
  }
  return { error: String(e) };
}

/**
 * Auftragsfoto-Objekt (snake_case → camelCase via `mapAuftragFotoFromDb`).
 *
 * Exportiert, damit konsumierende Komponenten (FotoDoku, EigeneFotoDoku)
 * sauber typisieren können. Bei Bedarf später nach `types.ts` verschieben.
 *
 * `dataUrl` ist ein Frontend-only Feld für den localStorage-Fallback (wenn
 * kein User angemeldet ist — dann werden Fotos als Data-URL im LS gehalten).
 */
export interface AuftragFoto {
  id: string;
  fahrerId?: string;
  auftragId?: string;
  stoppKey?: string;
  filePath?: string;
  notiz?: string;
  signedUrl?: string | null;
  createdAt?: string;

  /** Nur im localStorage-Fallback gesetzt — Base64 Data-URL. */
  dataUrl?: string;
  /** Legacy-Feld aus localStorage: Timestamp-String. */
  zeit?: string;
}

/**
 * Payload-Types für ApiResult<T> — die "T"-Inhalte der Storage-Funktionen.
 * Die tatsächliche Shape ist immer ApiResult<X>, d.h. `{ data: X }` oder
 * `{ error: string }`. Diese Interfaces sind nur die inneren Payloads.
 */
interface UploadPayload {
  path: string;
  name?: string;
}

interface SignedUrlPayload {
  url: string;
}

// ════════════════════════════════════════════════════════════════════
// FILE UPLOADS — Interner Helper
// ════════════════════════════════════════════════════════════════════

/**
 * Generische Upload-Funktion für beliebige Storage-Buckets.
 *
 * Validiert Größe und MIME-Typ über `validateUploadFile` (compile-time
 * über `BucketName` abgesichert) und sanitiert den Dateinamen gegen
 * Path-Traversal. Der Upload-Pfad wird mit Timestamp-Prefix versehen,
 * um Kollisionen zu vermeiden.
 *
 * **Intern** — wird nur von `uploadBeladungFoto` und `uploadLieferschein`
 * verwendet, nicht exportiert.
 */
async function uploadFile(
  bucket: BucketName,
  folder: string,
  file: File,
): Promise<ApiResult<{ path: string; name: string; size: number }>> {
  if (!_sb) return { error: NO_CLIENT_ERROR.message };
  const client = _sb;
  try {
    const valErr = await validateUploadFileDeep(bucket, file);
    if (valErr) return errResult(valErr);
    const safeName = sanitizeFileName(file.name);
    const path = folder + '/' + Date.now() + '_' + safeName;
    const r = await client.storage.from(bucket).upload(path, file);
    if (r.error) return { error: r.error.message };
    return { data: { path: r.data.path, name: file.name, size: file.size } };
  } catch (e: unknown) {
    return errResult(e);
  }
}

// ════════════════════════════════════════════════════════════════════
// UPLOAD-WRAPPER (über api() aufgerufen → {data, error}-Shape)
// ════════════════════════════════════════════════════════════════════

/**
 * Lädt ein Beladungsfoto für einen Marktplatz-Auftrag hoch und setzt den
 * Auftrag automatisch auf Status "beladen". Speichert Pfad und Datum im
 * Auftragsdatensatz.
 *
 * Wird über `api()` aufgerufen. Caller liest das Result nur als boolean
 * ("hat funktioniert?"), die `data`-Felder werden ignoriert.
 */
export async function uploadBeladungFoto(
  userId: string,
  auftragId: string,
  file: File,
): Promise<ApiResult<UploadPayload>> {
  const r = await uploadFile('delivery-photos', userId, file);
  if (r.error !== undefined) return { error: r.error };
  // Nach dem Check ist r.data garantiert definiert. Wert einfrieren für
  // closure-safety — after-await narrow überlebt kein await.
  const uploadPath = r.data.path;
  // Audit-074 (16.05.2026, Befund #1): Update-Fehler nicht ignorieren.
  // Vorher: Result von updateAuftrag wurde weggeworfen → bei Fehler
  // (RLS, Constraint, Netzwerk) blieb das Foto im Bucket, der Auftrag-
  // Status unverändert, das Frontend sah aber "Success". Jetzt: Bei
  // Update-Fehler das gerade hochgeladene Foto wieder aus dem Bucket
  // löschen (best-effort) und Fehler zurückgeben.
  const upd = await updateAuftrag(auftragId, {
    beladungFotoPath: uploadPath,
    beladungFotoName: file.name,
    beladungFotoDatum: todayLocal(),
    status: 'beladen',
  });
  if (upd.error) {
    // Storage-Rollback: best-effort, blockiert die Fehlermeldung nicht.
    // Wenn dieser Rollback fehlschlägt, bleibt ein Waisen-Foto im Bucket,
    // aber das ist besser als ein verwaister DB-Verweis.
    if (_sb) {
      try {
        await _sb.storage.from('delivery-photos').remove([uploadPath]);
      } catch {
        /* fail-silent — ein Cron-Cleanup-Job räumt sowas später ab */
      }
    }
    return { error: errMsg(upd.error) };
  }
  return { data: { path: uploadPath, name: file.name } };
}

/**
 * Lädt einen Lieferschein für einen Marktplatz-Auftrag hoch und setzt
 * den Auftrag automatisch auf Status "geliefert".
 *
 * Wird über `api()` aufgerufen — siehe `uploadBeladungFoto`.
 */
export async function uploadLieferschein(
  userId: string,
  auftragId: string,
  file: File,
  empfangenVon?: string,
): Promise<ApiResult<UploadPayload>> {
  const r = await uploadFile('delivery-notes', userId, file);
  if (r.error !== undefined) return { error: r.error };
  const uploadPath = r.data.path;
  // Audit-074 (16.05.2026, Befund #1): Update-Fehler nicht ignorieren.
  // Siehe uploadBeladungFoto oben für die ausführliche Begründung.
  const upd = await updateAuftrag(auftragId, {
    lieferscheinPath: uploadPath,
    lieferscheinName: file.name,
    lieferscheinDatum: todayLocal(),
    // Empfängername (optional, Migration 099) — Teil des Liefernachweises
    empfangenVon: (empfangenVon || '').trim().slice(0, 200),
    status: 'geliefert',
  });
  if (upd.error) {
    if (_sb) {
      try {
        await _sb.storage.from('delivery-notes').remove([uploadPath]);
      } catch {
        /* fail-silent — Cron-Cleanup räumt sowas später ab */
      }
    }
    return { error: errMsg(upd.error) };
  }
  return { data: { path: uploadPath, name: file.name } };
}

// ════════════════════════════════════════════════════════════════════
// FRACHTBRIEF LESEN (Mistral OCR über Edge Function, 20.10.2026)
// ════════════════════════════════════════════════════════════════════

/** Ein erkanntes Packstück (Maße in cm, Gewicht in kg, je nur die Zahl). */
export interface FrachtbriefPackstueck {
  laenge: string;
  breite: string;
  hoehe: string;
  gewicht: string;
  bemerkung: string;
}

/**
 * Felder, die `frachtbrief-lesen` aus Frachtbrief-Fotos erkennt. Leerer
 * Text = nicht gefunden. Gleiche Form wie in der Edge Function.
 */
export interface FrachtbriefFelder {
  kundeName: string;
  kundeAdresse: string;
  kundePlz: string;
  kundeOrt: string;
  kundeAnsprech: string;
  kundeTel: string;
  kundeEmail: string;
  abhol: string;
  ziel: string;
  /** JJJJ-MM-TT */
  datum: string;
  /** HH:MM–HH:MM oder HH:MM */
  zeit: string;
  entladeDatum: string;
  entladeZeit: string;
  titel: string;
  gewicht: string;
  groesse: string;
  /** Zahl mit Punkt, ohne Währung */
  preis: string;
  notizen: string;
  packstuecke: FrachtbriefPackstueck[];
}

/** Ergebnis von {@link frachtbriefLesen}: Felder ODER Fehlercode. */
export interface FrachtbriefErgebnis {
  felder: FrachtbriefFelder | null;
  /**
   * Fehlercode der Edge Function (`limit_stunde`, `limit_tag`,
   * `ungueltige_fotos`, `nicht_eingerichtet`, `keine_berechtigung`,
   * `lesefehler` …) oder technischer Fehlertext.
   */
  fehler: string | null;
}

/**
 * Schickt 1–3 Frachtbrief-Fotos (JPEG-Data-URLs) an die Edge Function
 * `frachtbrief-lesen` (Mistral OCR) und liefert die erkannten Felder.
 *
 * Eigenes Ergebnis-Shape statt `ApiResult`, weil der Aufrufer
 * (`FahrerEigeneAuftraege`) je Fehlercode einen eigenen Text zeigt.
 * Bei Status ≠ 2xx steckt unsere Antwort `{ error }` im `context`.
 *
 * @param fotos - JPEG-Data-URLs, bereits verkleinert
 */
export async function frachtbriefLesen(fotos: string[]): Promise<FrachtbriefErgebnis> {
  if (!_sb) return { felder: null, fehler: NO_CLIENT_ERROR.message };
  try {
    const res = await _sb.functions.invoke('frachtbrief-lesen', { body: { fotos } });
    if (res.error) {
      const ctx = (res.error as { context?: { json?: () => Promise<unknown> } }).context;
      if (ctx && typeof ctx.json === 'function') {
        try {
          const b = (await ctx.json()) as { error?: unknown } | null;
          if (b && typeof b.error === 'string') return { felder: null, fehler: b.error };
        } catch {
          /* keine JSON-Antwort */
        }
      }
      return { felder: null, fehler: res.error.message || String(res.error) };
    }
    const raw: unknown = res.data;
    const data = (typeof raw === 'string' ? JSON.parse(raw) : raw) as {
      felder?: unknown;
      error?: unknown;
    } | null;
    if (data && typeof data.error === 'string') return { felder: null, fehler: data.error };
    if (data && data.felder && typeof data.felder === 'object') {
      return { felder: data.felder as FrachtbriefFelder, fehler: null };
    }
    return { felder: null, fehler: 'lesefehler' };
  } catch (e: unknown) {
    return { felder: null, fehler: errMsg(e) };
  }
}

// ════════════════════════════════════════════════════════════════════
// KYC DOKUMENTE
// ════════════════════════════════════════════════════════════════════

/**
 * Lädt ein KYC-Dokument für einen User hoch und aktualisiert das Profil.
 *
 * Die Datei liegt unter `{userId}/{docKey}_{timestamp}_{safeName}` und ist
 * nur über Signed URLs zugänglich.
 *
 * Wird teilweise über `api()` aufgerufen, teilweise direkt — Caller in
 * `useAuth.ts` nutzt das Result nur als Promise (in `Promise.all().catch()`).
 * Daher `{data, error}`-Shape.
 *
 * KYC-Audit 089, Befund #1 (17.05.2026):
 * Race Condition behoben — `updateProfile`-Return wird jetzt geprüft.
 * Wenn das Profil-Update fehlschlägt, wird die neu hochgeladene Datei
 * wieder aus dem Bucket entfernt (Rollback). Vorher: alte Datei wurde
 * gelöscht obwohl der neue Pfad nicht im Profil landete → User hatte
 * weder altes noch neues KYC-Dokument.
 */
export async function uploadKycDoc(
  userId: string,
  docKey: 'gewerbeschein' | 'versicherung' | string,
  file: File,
): Promise<ApiResult<UploadPayload>> {
  if (!_sb) return { error: NO_CLIENT_ERROR.message };
  const client = _sb;
  try {
    const valErr = await validateUploadFileDeep('kyc-documents', file);
    if (valErr) return errResult(valErr);

    // Block 2e Teil 2a H2 (21.04.2026):
    // Alte Datei-Referenz VOR dem Upload lesen. Wenn der User ein Dokument
    // re-uploaded, soll die alte Version nach erfolgreichem neuen Upload
    // aus dem Bucket entfernt werden. Grund: DSGVO (keine Daten länger als
    // nötig aufbewahren) + Storage-Kosten + Audit-Klarheit (nur eine
    // aktive Version pro Feld).
    const fieldMap: Record<string, 'kycGewerbeschein' | 'kycVersicherung' | 'kycBonitaetsnachweis'> = {
      gewerbeschein: 'kycGewerbeschein',
      versicherung: 'kycVersicherung',
      bonitaetsnachweis: 'kycBonitaetsnachweis',
    };
    const profileField = fieldMap[docKey];

    let oldPath: string | null = null;
    let currentReviews: Record<string, DokReviewEntry> = {};

    if (profileField) {
      // Ein DB-Roundtrip für beides: alter Pfad + dokReviews
      const profRes = await getProfile(userId);
      const profData = profRes.data;
      if (profData) {
        // Umweg über `unknown` ist nötig, weil Profile kein Record-Index-
        // Signature hat (strikt typisiertes Interface). Der Wert ist
        // garantiert ein string oder undefined.
        const raw: unknown =
          (profData as unknown as Record<string, unknown>)[profileField];
        if (typeof raw === 'string' && raw.length > 0) {
          oldPath = raw;
        }
        currentReviews = profData.dokReviews || {};
      }
    }

    // Neuen Pfad generieren und hochladen
    const safeName = sanitizeFileName(file.name);
    const path = userId + '/' + docKey + '_' + Date.now() + '_' + safeName;
    const r = await client.storage
      .from('kyc-documents')
      .upload(path, file, { upsert: true });
    if (r.error) return { error: r.error.message };

    const newPath = r.data.path;

    // Pfad im Profil speichern + alten Review für diesen DocKey auto-zurücksetzen
    // (Re-Upload = neue Prüfung; alte "Abgelehnt"/"Gueltig"-Review ist stale)
    if (profileField) {
      // KYC-Audit 089 #1 FIX:
      // updateProfile-Return prüfen. Bei Fehler die neu hochgeladene Datei
      // wieder aus dem Bucket entfernen → Profil bleibt konsistent
      // (zeigt weiter auf alte intakte Datei, falls vorhanden).
      //
      // Schritt 1: Pfad im Profil aktualisieren. dokReviews wird NICHT
      // hier mit-geupdated, weil der protect_admin_fields-Trigger jegliche
      // User-seitige Änderung am dok_reviews-Feld blockt. Stattdessen
      // läuft der Review-Reset über die dedizierte RPC (Schritt 3).
      const updRes = await updateProfile(userId, {
        [profileField]: newPath,
      });

      if (updRes.error) {
        // ROLLBACK: Neue Datei wieder löschen, sonst Waisen-File im Bucket
        try {
          await client.storage.from('kyc-documents').remove([newPath]);
        } catch {
          /* Rollback best-effort — falls auch das fehlschlägt, bleibt
             im schlimmsten Fall eine Waisen-Datei zurück, aber das Profil
             ist konsistent (zeigt auf alte Datei). Cleanup-Cron räumt
             Waisen später auf. */
        }
        return errResult(updRes.error);
      }

      // Schritt 2: Alte Datei best-effort löschen. Fehler werden nicht propagiert:
      // - Der Upload ist erfolgreich, der neue Pfad ist im Profil
      // - Eine fehlgeschlagene Storage-Delete-Operation (z. B. weil die
      //   Datei längst weg ist) darf den User nicht blockieren
      // - Orphaned Files können später via Cleanup-Cron aufgeräumt werden
      if (oldPath && oldPath !== newPath) {
        try {
          await client.storage.from('kyc-documents').remove([oldPath]);
        } catch {
          /* best-effort — alte Datei aufräumen, aber Upload nicht blockieren */
        }
      }

      // Schritt 3: Review-Reset via RPC (Migration 082, KYC-Audit 089 #1
      // follow-up). Der protect_admin_fields-Trigger blockt direkte
      // UPDATEs auf dok_reviews durch den User; die RPC umgeht das mit
      // SECURITY DEFINER und prüft server-seitig dass kyc_<docKey>
      // gefüllt ist (was nach Schritt 1 garantiert ist).
      //
      // Nur ausführen wenn ein Review-Eintrag existierte — vermeidet
      // unnötige RPC-Calls. Fehler werden geloggt aber nicht propagiert:
      // Upload + Pfad-Update sind bereits erfolgreich, ein stehengebliebener
      // Review-Eintrag stört nur die UI, nicht die Datenintegrität.
      if (docKey in currentReviews && (docKey === 'gewerbeschein' || docKey === 'versicherung' || docKey === 'bonitaetsnachweis')) {
        try {
          const resetRes = await client.rpc('reset_dok_review_for_self', {
            p_doc_key: docKey,
          });
          if (resetRes.error) {
            /* eslint-disable-next-line no-console */
            console.warn('reset_dok_review_for_self failed:', resetRes.error.message);
          }
        } catch {
          /* best-effort — Review-Reset ist UX-Verbesserung, nicht kritisch */
        }
      }
    }
    return { data: { path: newPath, name: file.name } };
  } catch (e: unknown) {
    return errResult(e);
  }
}
/**
 * Erzeugt eine zeitlich begrenzte Signed URL für ein KYC-Dokument.
 *
 * @param path            Pfad im kyc-documents-Bucket
 * @param lifetimeSeconds URL-Gültigkeit in Sekunden (Default 300 = 5 Min).
 *                        Für länger andauernde Admin-Reviews kann hier
 *                        z. B. 3600 (1 h) übergeben werden.
 *
 * **Aufruf-Pattern:** Direkter Aufruf in `ProfilSeite.handleViewKycDoc`
 * (User klickt Doc, will spezifischen Fehlertext per Toast sehen). In
 * `KycUserBody` ebenfalls direkt aufgerufen mit eigenem Error-Handling.
 * Daher Ausnahme vom `apiSilent()`-Standard für Read-Operations.
 */
export async function getKycUrl(
  path: string,
  lifetimeSeconds: number = 300,
): Promise<ApiResult<SignedUrlPayload>> {
  if (!_sb) return { error: NO_CLIENT_ERROR.message };
  const client = _sb;
  try {
    const r = await client.storage
      .from('kyc-documents')
      .createSignedUrl(path, lifetimeSeconds);
    if (r.error) return { error: r.error.message };
    return { data: { url: r.data.signedUrl } };
  } catch (e: unknown) {
    return errResult(e);
  }
}

/**
 * Allgemeine Helper-Funktion: Signed URL für eine Datei in beliebigem
 * Bucket.
 *
 * @param bucket          Bucket-Name
 * @param path            Dateipfad im Bucket
 * @param lifetimeSeconds URL-Gültigkeit in Sekunden (Default 300 = 5 Min)
 *
 * **Aufruf-Pattern:** Read-Operation, wird über `apiSilent()` aufgerufen — Errors werden via `logError()` geloggt, kein UI-Feedback (silent fail).
 */
export async function getSignedUrl(
  bucket: BucketName,
  path: string,
  lifetimeSeconds: number = 300,
): Promise<ApiResult<SignedUrlPayload>> {
  if (!_sb) return { error: NO_CLIENT_ERROR.message };
  const client = _sb;
  try {
    const r = await client.storage
      .from(bucket)
      .createSignedUrl(path, lifetimeSeconds);
    if (r.error) return { error: r.error.message };
    return { data: { url: r.data.signedUrl } };
  } catch (e: unknown) {
    return errResult(e);
  }
}

// ════════════════════════════════════════════════════════════════════
// AUFTRAG-FOTOS
// ════════════════════════════════════════════════════════════════════

/**
 * Lädt alle Dokumentations-Fotos eines Auftrags für einen Fahrer und
 * holt gleichzeitig für jedes Foto eine Signed URL (5 Min gültig).
 *
 * **Aufruf-Pattern:** Read-Operation, wird über `apiSilent()` aufgerufen — Errors werden via `logError()` geloggt, kein UI-Feedback (silent fail).
 */
export async function getAuftragFotos(
  fahrerId: string,
  auftragId: string,
): Promise<ApiResult<AuftragFoto[]>> {
  if (!_sb) return { error: NO_CLIENT_ERROR.message };
  const client = _sb;
  try {
    const r = await client
      .from('auftrag_fotos')
      .select('*')
      .eq('fahrer_id', fahrerId)
      .eq('auftrag_id', auftragId)
      .order('created_at', { ascending: true });
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    const fotos = rows
      .map((row) => mapAuftragFotoFromDb(row))
      .filter((row): row is Record<string, unknown> => row !== null) as unknown as AuftragFoto[];

    // Signed URLs holen (5 Min gültig)
    for (let i = 0; i < fotos.length; i++) {
      if (fotos[i].filePath) {
        try {
          const sr = await client.storage
            .from('delivery-photos')
            .createSignedUrl(fotos[i].filePath as string, 300);
          if (sr.data) fotos[i].signedUrl = sr.data.signedUrl;
        } catch {
          /* ignore — Foto wird ohne signedUrl angezeigt */
        }
      }
    }
    return { data: fotos };
  } catch (e: unknown) {
    return errResult(e);
  }
}

/**
 * Lädt ein Dokumentations-Foto für einen bestimmten Stopp eines Auftrags
 * hoch. Wird aus der Kamera oder html2canvas-Aufnahmen aufgerufen, daher
 * Blob statt File. Max. 10 MB pro Bild. Speichert einen Datenbank-Eintrag
 * und liefert direkt eine signedUrl mit zurück.
 *
 * Wird über `api()` aufgerufen. Caller liest das ganze Foto-Objekt aus
 * `res.data` — daher `{data, error}`-Shape mit dem AuftragFoto im data.
 */
export async function uploadAuftragFoto(
  fahrerId: string,
  auftragId: string,
  stoppKey: string,
  blob: Blob,
  notiz?: string,
): Promise<ApiResult<AuftragFoto>> {
  if (!_sb) return { error: NO_CLIENT_ERROR.message };
  const client = _sb;
  try {
    // Block 2e Teil 2a K1 (21.04.2026):
    // Vorher wurde nur der blob.size geprüft — blob.type wurde ungeprüft als
    // contentType durchgereicht. Ein Angreifer konnte einen Blob mit
    // gefälschtem type einschleusen (z. B. PDF als image/png deklariert).
    // Die Server-seitige MIME-Restriction (Migration 043) fängt nur grobe
    // Typ-Verstöße ab. validateUploadBlobDeep macht den echten Magic-Bytes-
    // Check: die ersten 16 Bytes werden gegen bekannte Signaturen verglichen
    // und müssen zur Bucket-Whitelist passen.
    const valErr = await validateUploadBlobDeep('delivery-photos', blob);
    if (valErr) return errResult(valErr);
    const ext = blob.type === 'image/png' ? '.png' : '.jpg';
    const fileName =
      Date.now() + '_' + Math.random().toString(36).slice(2, 6) + ext;
    const filePath = fahrerId + '/fotos/' + fileName;
    const ur = await client.storage
      .from('delivery-photos')
      .upload(filePath, blob, { contentType: blob.type });
    if (ur.error) return { error: ur.error.message };

    const d = {
      fahrer_id: fahrerId,
      auftrag_id: auftragId,
      stopp_key: stoppKey || '',
      file_path: filePath,
      notiz: notiz || '',
    };
    const r = await client.from('auftrag_fotos').insert(d).select().single();
    if (r.error) return { error: r.error.message };
    const foto = r.data
      ? (mapAuftragFotoFromDb(r.data as Record<string, unknown>) as AuftragFoto | null)
      : null;
    if (!foto) return { error: 'Foto konnte nicht angelegt werden' };

    // Signed URL direkt mitholen
    if (foto.filePath) {
      const sr = await client.storage
        .from('delivery-photos')
        .createSignedUrl(foto.filePath, 300);
      if (sr.data) foto.signedUrl = sr.data.signedUrl;
    }
    return { data: foto };
  } catch (e: unknown) {
    return errResult(e);
  }
}

/**
 * Löscht ein Auftragsfoto: zuerst aus dem Storage-Bucket, dann den
 * DB-Eintrag. Ignoriert Storage-Fehler (z.B. wenn die Datei bereits
 * weg ist), solange der DB-Eintrag verschwindet.
 *
 * Wird über `api()` aufgerufen. Caller checkt nur den Erfolg.
 */
export async function deleteAuftragFoto(
  id: string,
  filePath?: string,
): Promise<ApiResult<null>> {
  if (!_sb) return { error: NO_CLIENT_ERROR.message };
  const client = _sb;
  try {
    if (filePath) {
      await client.storage.from('delivery-photos').remove([filePath]);
    }
    const r = await client.from('auftrag_fotos').delete().eq('id', id);
    if (r.error) return { error: r.error.message };
    return { data: null };
  } catch (e: unknown) {
    return errResult(e);
  }
}

/**
 * Liefert für einen Fahrer ein Objekt `{ auftragId: count }` mit der
 * Anzahl der Fotos pro Auftrag. Wird für die Navigations-Badges verwendet.
 *
 * **Aufruf-Pattern:** Read-Operation, wird über `apiSilent()` aufgerufen — Errors werden via `logError()` geloggt, kein UI-Feedback (silent fail).
 */
export async function getFahrerFotoCounts(
  fahrerId: string,
): Promise<ApiResult<Record<string, number>>> {
  if (!_sb) return { error: NO_CLIENT_ERROR.message };
  const client = _sb;
  try {
    const r = await client
      .from('auftrag_fotos')
      .select('auftrag_id, stopp_key')
      .eq('fahrer_id', fahrerId);
    const rows = (r.data || []) as Array<{ auftrag_id: string }>;
    const counts: Record<string, number> = {};
    rows.forEach((row) => {
      const key = row.auftrag_id;
      counts[key] = (counts[key] || 0) + 1;
    });
    return { data: counts };
  } catch (e: unknown) {
    return errResult(e);
  }
}

// ════════════════════════════════════════════════════════════════════
// FIRMA: FOTOS LESEN (read-only via RLS)
// ════════════════════════════════════════════════════════════════════

/**
 * Lädt alle Fotos eines Auftrags für die Firma-Sicht (Firma darf die
 * Fotos ihres eigenen Auftrags sehen, RLS regelt den Zugriff).
 *
 * **Aufruf-Pattern:** Read-Operation, wird über `apiSilent()` aufgerufen — Errors werden via `logError()` geloggt, kein UI-Feedback (silent fail).
 */
export async function getAuftragFotosByAuftrag(
  auftragId: string,
): Promise<ApiResult<AuftragFoto[]>> {
  if (!_sb) return { error: NO_CLIENT_ERROR.message };
  const client = _sb;
  try {
    const r = await client
      .from('auftrag_fotos')
      .select('*')
      .eq('auftrag_id', auftragId)
      .order('created_at', { ascending: true });
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    const fotos = rows
      .map((row) => mapAuftragFotoFromDb(row))
      .filter((row): row is Record<string, unknown> => row !== null) as unknown as AuftragFoto[];

    for (let i = 0; i < fotos.length; i++) {
      if (fotos[i].filePath) {
        try {
          const sr = await client.storage
            .from('delivery-photos')
            .createSignedUrl(fotos[i].filePath as string, 300);
          if (sr.data) fotos[i].signedUrl = sr.data.signedUrl;
        } catch {
          /* ignore — Foto wird ohne signedUrl angezeigt */
        }
      }
    }
    return { data: fotos };
  } catch (e: unknown) {
    return errResult(e);
  }
}

/**
 * Foto-Count-Map für mehrere Firma-Aufträge auf einmal (für Listen-Badges).
 *
 * **Aufruf-Pattern:** Read-Operation, wird über `apiSilent()` aufgerufen — Errors werden via `logError()` geloggt, kein UI-Feedback (silent fail).
 */
export async function getFirmaFotoCounts(
  auftragIds: string[],
): Promise<ApiResult<Record<string, number>>> {
  if (!_sb) return { error: NO_CLIENT_ERROR.message };
  const client = _sb;
  try {
    if (!auftragIds || auftragIds.length === 0) return { data: {} };
    const r = await client
      .from('auftrag_fotos')
      .select('auftrag_id')
      .in('auftrag_id', auftragIds);
    const rows = (r.data || []) as Array<{ auftrag_id: string }>;
    const counts: Record<string, number> = {};
    rows.forEach((row) => {
      counts[row.auftrag_id] = (counts[row.auftrag_id] || 0) + 1;
    });
    return { data: counts };
  } catch (e: unknown) {
    return errResult(e);
  }
}
